/**
 * Bookings API Service
 * Handles booking-related API calls
 */

import { get, post } from "./client";
import { BookingRecord } from "../../types";

// === Request Types ===

export interface CalculateBookingRequest {
  ticketId: string;
  participant: number;
  voucherCode?: string;
}

export interface CreateBookingRequest {
  ticketId: string;
  participant: number;
  visitDate: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  voucherCode?: string;
  paymentMethod?: "online" | "loket";
}

// === Response Types ===

export interface BookingCalculation {
  ticket: {
    id: string;
    name: string;
    price: number;
  };
  participant: number;
  grossAmount: number;
  isBulkDiscount: boolean;
  bulkDiscountPercent: number;
  bulkDiscountAmount: number;
  voucherDiscount: number;
  totalDiscount: number;
  totalAmount: number;
  voucher: {
    code: string;
    discountType: string;
    discountValue: number;
  } | null;
}

interface CalculateResponse {
  success: boolean;
  data: BookingCalculation;
  message?: string;
}

interface CreateBookingResponse {
  success: boolean;
  message: string;
  data: {
    bookingCode: string;
    customer: {
      name: string;
      email: string;
    };
    ticket: {
      name: string;
      type: string;
    };
    participant: number;
    visitDate: string;
    totalAmount: number;
    paymentStatus: string;
  };
}

interface BookingDetailResponse {
  success: boolean;
  data: {
    id: string;
    booking_code?: string;
    bookingCode?: string;
    code?: string;
    participant: number;
    visit_date?: string;
    visitDate?: string;
    payment_status?: string;
    paymentStatus?: string;
    gross_amount?: number;
    grossAmount?: number;
    total_amount?: number;
    totalAmount?: number;
    created_at?: string;
    createdAt?: string;
    customer: {
      id: string;
      full_name?: string;
      fullName?: string;
      email: string;
      phone: string;
    };
    ticket: {
      id: string;
      name: string;
      type: string;
    };
    voucher?: {
      code: string;
    } | null;
  };
}

export interface VoucherValidation {
  code: string;
  discountType: "percent" | "fixed";
  discountValue: number;
  partner: boolean;
  partnerName: string | null;
}

interface VoucherValidationResponse {
  success: boolean;
  data: VoucherValidation;
  message?: string;
}

// === API Functions ===

/**
 * Calculate booking price with optional voucher
 */
export async function calculateBooking(
  data: CalculateBookingRequest
): Promise<BookingCalculation> {
  const response = await post<CalculateResponse>(
    "/public/bookings/calculate",
    data
  );
  return response.data;
}

/**
 * Create a new booking
 */
export async function createBooking(
  data: CreateBookingRequest
): Promise<CreateBookingResponse["data"]> {
  const response = await post<CreateBookingResponse>("/public/bookings", data);
  return response.data;
}

/**
 * Get booking by code
 */
export async function getBookingByCode(
  code: string
): Promise<BookingRecord | null> {
  try {
    const response = await get<BookingDetailResponse>(
      `/public/bookings/${code}`,
      undefined,
      undefined,
      false
    );
    const booking = response.data;

    const bookingCode =
      booking.booking_code || booking.bookingCode || booking.code;
    const visitDate = booking.visit_date || booking.visitDate;
    const paymentStatus =
      booking.payment_status || booking.paymentStatus || "pending";
    const totalAmount = booking.total_amount ?? booking.totalAmount ?? 0;
    const createdAt =
      booking.created_at || booking.createdAt || new Date().toISOString();
    const customerName =
      booking.customer.full_name || booking.customer.fullName || "";

    if (!bookingCode) {
      throw new Error("Booking code not found in response");
    }

    // Transform API response to BookingRecord format
    return {
      id: booking.id,
      code: bookingCode,
      customerName,
      email: booking.customer.email,
      phone: booking.customer.phone,
      date: visitDate,
      category: "Personal", // Default from API
      ticketId: booking.ticket.id,
      ticketType: booking.ticket.name,
      pax: booking.participant,
      totalPrice: totalAmount,
      status: transformPaymentStatus(paymentStatus),
      paymentMethod: "QRIS", // Default
      bookingDate: createdAt,
      voucherCode: booking.voucher?.code,
    };
  } catch (error) {
    console.error("Failed to fetch booking:", error);
    return null;
  }
}

/**
 * Get booking by email
 */
export async function getBookingByEmail(
  email: string
): Promise<BookingRecord | null> {
  try {
    const response = await get<{ success: boolean; data: any }>(
      `/public/bookings?email=${email}`,
      undefined,
      undefined,
      false
    );
    const booking = response.data;
    if (!booking) return null;
    const bookingCode =
      booking.booking_code || booking.bookingCode || booking.code;
    const visitDate = booking.visit_date || booking.visitDate;
    const paymentStatus =
      booking.payment_status || booking.paymentStatus || "pending";
    const totalAmount = booking.total_amount ?? booking.totalAmount ?? 0;
    const createdAt =
      booking.created_at || booking.createdAt || new Date().toISOString();
    const customerName =
      booking.customer?.full_name || booking.customer?.fullName || "";
    return {
      id: booking.id,
      code: bookingCode,
      customerName,
      email: booking.customer?.email,
      phone: booking.customer?.phone,
      date: visitDate,
      category: "Personal",
      ticketId: booking.ticket?.id,
      ticketType: booking.ticket?.name,
      pax: booking.participant,
      totalPrice: totalAmount,
      status: transformPaymentStatus(paymentStatus),
      paymentMethod: "QRIS",
      bookingDate: createdAt,
      voucherCode: booking.voucher?.code,
    };
  } catch (error) {
    console.error("Failed to fetch booking by email:", error);
    return null;
  }
}

/**
 * Transform payment status from API to frontend format
 */
function transformPaymentStatus(status: string): BookingRecord["status"] {
  const statusMap: Record<string, BookingRecord["status"]> = {
    pending: "PENDING",
    paid: "PAID",
    completed: "COMPLETED",
    cancelled: "CANCELLED",
  };
  return statusMap[status.toLowerCase()] || "PENDING";
}

/**
 * Validate a voucher code
 */
export async function validateVoucher(
  code: string,
  ticketId: string
): Promise<VoucherValidation | null> {
  try {
    const response = await post<VoucherValidationResponse>(
      "/public/vouchers/validate",
      {
        code,
        ticketId,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Voucher validation failed:", error);
    return null;
  }
}

export interface RandomVoucher {
  code: string;
  partnerName: string | null;
  discountType: "percent" | "fixed";
  discountValue: number;
}

interface RandomVoucherResponse {
  success: boolean;
  data: RandomVoucher | null;
}

/**
 * Get a random active voucher for rotating display
 */
export async function getRandomVoucher(): Promise<RandomVoucher | null> {
  try {
    const response = await get<RandomVoucherResponse>(
      "/public/vouchers/random",
      undefined,
      undefined,
      false
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch random voucher:", error);
    return null;
  }
}
